import React, { createContext, useState, useEffect, useCallback } from 'react';

// SERVICES
import { getSession, login as authLogin, logout as authLogout } from '../services/authService';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
    const [loading, setLoading] = useState(true);
    const [authenticated, setAuthenticated] = useState(false);
    const [email, setEmail] = useState(null);
    const [isInternal, setIsInternal] = useState(false);
    const [isExternal, setIsExternal] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);

    // Les rôles sont ceux calculés par le backend à partir des groupes AD, puis
    // scellés dans le jeton de session. Le front ne fait que les refléter : il
    // ne les décide plus, et l'API ne lui fait plus confiance sur ce point.
    const applySession = useCallback((session) => {
        if (!session) {
            setEmail(null);
            setIsInternal(false);
            setIsExternal(false);
            setIsAdmin(false);
            setAuthenticated(false);
            return;
        }

        setEmail(session.email);
        setIsInternal(Boolean(session.roles?.internal));
        setIsExternal(Boolean(session.roles?.external));
        setIsAdmin(Boolean(session.roles?.admin));
        setAuthenticated(true);
    }, []);

    // Au démarrage, une seule question au serveur : « qui suis-je ? ». S'il n'y
    // a pas de session valide, on retombe sur le formulaire de connexion.
    useEffect(() => {
        let cancelled = false;

        const restoreSession = async () => {
            const session = await getSession();
            if (cancelled) return;

            applySession(session);
            setLoading(false);
        };

        restoreSession();

        return () => { cancelled = true; };
    }, [applySession]);

    const login = async (userEmail, password) => {
        try {
            const result = await authLogin(userEmail, password);

            if (result.success) {
                applySession({ email: result.email, roles: result.roles });
                return { success: true, message: "ok" };
            }

            applySession(null);
            // Retourner l'erreur avec le message détaillé
            return {
                success: false,
                error: result.error || "UNKNOWN_ERROR",
                message: result.message || "Une erreur est survenue lors de la connexion."
            };
        } catch (error) {
            console.error("Erreur lors de la connexion :", error);
            applySession(null);
            return {
                success: false,
                error: "UNKNOWN_ERROR",
                message: "Une erreur inattendue s'est produite. Veuillez réessayer."
            };
        }
    };

    const logout = async () => {
        // La session vit côté serveur : sans cet appel, le cookie resterait
        // valide et un simple F5 reconnecterait l'utilisateur.
        await authLogout();
        applySession(null);
    };

    return (
        <AuthContext.Provider value={{ authenticated, email, isInternal, isExternal, isAdmin, login, logout, loading }}>
            {children}
        </AuthContext.Provider>
    );
}
